import { memo, React, useMemo } from 'react'
import {
  EyeInvisibleOutlined,
  FlagOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import { Space, Tooltip, Typography } from 'antd'
import {
  filter,
  get,
  includes,
  isEmpty,
  isEqual,
  isNil,
  join,
  map,
  omit,
  size,
} from 'lodash'
import PropTypes from 'prop-types'

import { areEqualByProps } from '../../../core/utils'

import { idPropType, nodePropType } from '../../prop-types'
import { StyledAlert } from '../styled'

import SearchHighlightedSpan from './search-highlighted-span'

const IngredientAutocheckTreeTitle = ({
  node,
  searchValue,
  groupedFlatTreeDataById,
  groupedIngredientsFromOtherSourcesById,
  treeCheckedKeys,
  includeComponentIngredients,
  treeNodeIdsToExclude,
}) => {
  const isExcluded = useMemo(
    () => includes(treeNodeIdsToExclude, node.id),
    [node.id, treeNodeIdsToExclude],
  )

  const isChecked = useMemo(
    () => includes(treeCheckedKeys, node.key),
    [node.key, treeCheckedKeys],
  )

  const nodeContent = (
    <Space>
      <Typography.Text
        type={
          includeComponentIngredients && !node.isWholeFood
            ? 'secondary'
            : undefined
        }
      >
        <SearchHighlightedSpan searchValue={searchValue}>
          {node.name}
        </SearchHighlightedSpan>
      </Typography.Text>
      {includeComponentIngredients && !node.isCurationEnabled && (
        <EyeInvisibleOutlined />
      )}
      {node.isVague && <FlagOutlined />}
    </Space>
  )

  let { source, sourceBadgeType } = get(
    groupedIngredientsFromOtherSourcesById,
    `${node.id}[0]`,
    {},
  )

  if (!isChecked && !isExcluded) {
    return nodeContent
  }

  if (
    includeComponentIngredients &&
    isNil(source) &&
    isNil(sourceBadgeType) &&
    (!node.isWholeFood || !isEmpty(node.componentIngredients))
  ) {
    sourceBadgeType = !isChecked ? 'info' : 'error'

    const sourcesOfIngredient = filter(node.componentIngredients, ({ id }) => {
      const ingredientId = get(groupedFlatTreeDataById, `${id}[0].id`)

      return includes(treeNodeIdsToExclude, ingredientId)
    })

    source = `${join(
      map(sourcesOfIngredient, ({ id }) =>
        get(groupedFlatTreeDataById, `${id}[0].name`),
      ),
      ' ; ',
    )}`
  }

  if (isEmpty(source)) {
    return nodeContent
  }

  return (
    <StyledAlert
      message={nodeContent}
      banner
      icon={
        <Tooltip
          title={
            <Typography.Text style={{ color: 'white' }}>
              Source: <br /> {source}
            </Typography.Text>
          }
        >
          <InfoCircleOutlined />
        </Tooltip>
      }
      type={sourceBadgeType}
    />
  )
}

IngredientAutocheckTreeTitle.propTypes = {
  node: nodePropType.isRequired,
  searchValue: PropTypes.string,
  groupedFlatTreeDataById: PropTypes.object.isRequired,
  groupedIngredientsFromOtherSourcesById: PropTypes.object,
  treeCheckedKeys: PropTypes.arrayOf(PropTypes.string.isRequired),
  includeComponentIngredients: PropTypes.bool,
  treeNodeIdsToExclude: PropTypes.arrayOf(idPropType),
}

IngredientAutocheckTreeTitle.defaultProps = {
  searchValue: '',
  groupedIngredientsFromOtherSourcesById: {},
  treeCheckedKeys: [],
  includeComponentIngredients: false,
  treeNodeIdsToExclude: [],
}

export default memo(
  IngredientAutocheckTreeTitle,
  ({ node: prevNode, ...restPrevious }, { node: nextNode, ...restNext }) =>
    isEqual(omit(prevNode, 'children'), omit(nextNode, 'children')) &&
    size(prevNode.children) === size(nextNode.children) &&
    areEqualByProps(restPrevious, restNext, [
      'treeNodeIdsToExclude',
      'searchValue',
      'groupedFlatTreeDataById',
      'groupedIngredientsFromOtherSourcesById',
      'treeCheckedKeys',
    ]),
)
